import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ChangeInstalledStockItem,
  InstalledStockItemsQuery,
  InstalledStockItemsQueryParameter
} from "../inventory/models/models";
import customerInstalledStockItemApi from "../shared/api/customerInstalledStockItemApi";
import { CustomerMachineState } from "./models/models";
import customerMachineryApi from "./api/customerMachineryApi";
import customerCategoriesApi from "../shared/api/customerCategoriesApi";
import customerManufacturersApi from "../shared/api/customerManufacturersApi";

export const getMachine = createAsyncThunk(
  "customerMachine/getMachine",
  async (id: string) => {
    return customerMachineryApi.getMachine(id);
  }
)

export const getInstalledStockItems = createAsyncThunk(
  "customerMachine/getInstalledStockItems",
  async (query: InstalledStockItemsQuery) => {
    return customerInstalledStockItemApi.getInstalledStockItems(query);
  }
)

export const installStockItems = createAsyncThunk(
  "customerMachine/installStockItems",
  async ({ machineId, model }: any) => {
    return customerInstalledStockItemApi.installStockItems(machineId, model);
  }
)

export const unlinkStockItems = createAsyncThunk(
  "customerMachine/unlinkStockItems",
  async ({ machineId, model }: any) => {
    return customerInstalledStockItemApi.unlinkStockItems(machineId, model);
  }
)

export const getCategories = createAsyncThunk(
  "customerMachine/getCategories",
  async () => {
    return customerCategoriesApi.getCategories();
  }
)

export const getManufacturers = createAsyncThunk(
  "customerMachine/getManufacturers",
  async () => {
    return customerManufacturersApi.getManufacturers();
  }
)

const initialMachineState: CustomerMachineState = {
  installedStockItemsQuery: {
    categoryId: undefined,
    search: undefined,
    machineId: '',
    sortBy: InstalledStockItemsQueryParameter.number
  },
  machine: undefined,
  categories: [],
  manufacturers: [],
  installedStockItems: undefined,
  loading: false,
  installedStockItemsLoading: false,
  success: undefined,
  error: undefined
};

const customerMachineSlice = createSlice({
  name: "customerMachine",
  initialState: initialMachineState,
  reducers: {
    changeInstalledStockItemsQuery: (state, action) => {
      state.installedStockItemsQuery = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMachine.fulfilled, (state, { payload }) => {
        state.machine = payload;
        state.loading = false;
      })
      .addCase(getMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getInstalledStockItems.pending, (state) => {
        state.installedStockItemsLoading = true;
      })
      .addCase(getInstalledStockItems.fulfilled, (state, { payload }) => {
        state.installedStockItems = payload;
        state.installedStockItemsLoading = false;
      })
      .addCase(getInstalledStockItems.rejected, (state, { error }) => {
        state.error = error;
        state.installedStockItemsLoading = false;
      });

    builder
      .addCase(getManufacturers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
        state.loading = false;
      })
      .addCase(getManufacturers.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })

    builder
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
        state.loading = false;
      })
      .addCase(getCategories.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })

    builder
      .addCase(installStockItems.pending, (state) => {
        state.installedStockItemsLoading = true;
      })
      .addCase(installStockItems.fulfilled, (state, { payload }) => {
        state.installedStockItemsLoading = false;
      })
      .addCase(installStockItems.rejected, (state, { error }) => {
        state.error = error;
        state.installedStockItemsLoading = false;
      })
  }
});

export const {
  changeInstalledStockItemsQuery,
  setSuccess,
  setLoading,
  setError
} = customerMachineSlice.actions;

export default customerMachineSlice.reducer;
